import { useRef, useEffect } from "react";

// ── Module-level singleton so the Maps script is only loaded once ──
let mapsReady = false;
let mapsLoading = false;
const mapsQueue = [];

export function loadMapsScript() {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
  if (!apiKey) return Promise.reject(new Error("REACT_APP_GOOGLE_MAPS_KEY not set"));
  if (mapsReady) return Promise.resolve();
  if (mapsLoading) return new Promise((res, rej) => mapsQueue.push({ res, rej }));
  mapsLoading = true;
  return new Promise((res, rej) => {
    mapsQueue.push({ res, rej });
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    s.async = true;
    s.defer = true;
    s.onload = () => {
      mapsReady = true;
      mapsLoading = false;
      mapsQueue.forEach(({ res }) => res());
      mapsQueue.length = 0;
    };
    s.onerror = () => {
      mapsLoading = false;
      mapsQueue.forEach(({ rej }) => rej(new Error("Maps script failed to load")));
      mapsQueue.length = 0;
    };
    document.head.appendChild(s);
  });
}

/**
 * PlacesAutocomplete — drop-in replacement for <input> on address fields.
 *
 * Props:
 *   value       string   — controlled value (synced into DOM input on external change)
 *   onChange    fn(address: string, lat: number|null, lng: number|null, meta?: { placeId, name })
 *   placeholder string
 *   style       object
 *   className   string
 *
 * When the user selects from the Google Places dropdown, onChange fires with
 * a fully verified address + lat/lng coordinates, plus a meta object carrying
 * the Google Place ID and place name (the canonical dedup key).
 * When the user types freely (no selection), onChange fires with the typed
 * text and lat/lng = null and meta = null.
 * Falls back to a plain <input> if REACT_APP_GOOGLE_MAPS_KEY is not set.
 */
export default function PlacesAutocomplete({ value, onChange, placeholder, style, className }) {
  const inputRef   = useRef();
  const acRef      = useRef();
  const onChangeFn = useRef(onChange);
  onChangeFn.current = onChange;

  // Sync external value into DOM input (e.g. when a saved location fills the field)
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = value || "";
    }
  }, [value]);

  // Load Maps script and attach Autocomplete widget once
  useEffect(() => {
    let cancelled = false;
    loadMapsScript()
      .then(() => {
        if (cancelled || !inputRef.current || acRef.current) return;
        acRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
          types: ["address"],
          fields: ["formatted_address", "geometry", "place_id", "name"],
        });
        acRef.current.addListener("place_changed", () => {
          const place = acRef.current.getPlace();
          if (!place?.geometry) return;
          onChangeFn.current(
            place.formatted_address,
            place.geometry.location.lat(),
            place.geometry.location.lng(),
            { placeId: place.place_id || null, name: place.name || "" },
          );
        });
      })
      .catch(() => {
        // No API key or script load failed — input works as a plain text box
      });
    return () => { cancelled = true; };
  }, []);

  return (
    <input
      ref={inputRef}
      defaultValue={value}
      placeholder={placeholder}
      style={style}
      className={className}
      onChange={(e) => onChangeFn.current(e.target.value, null, null)}
    />
  );
}
